// frontend/src/pages/RetailAccessory.jsx
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/UIComponents";

/**
 * HD Stock Table Maintenance Management Module
 * Left Side: Debounced inventory master keyword search stream registry filter list
 * Right Side: Atomic data modifications sheet controller dashboard layout
 */
export default function RetailAccessory() {
  const nav = useNavigate();

  // ---------- Left: search + filter list tracking states ----------
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [errorList, setErrorList] = useState("");

  // ---------- Right: dynamic layout entry structure form states ----------
  const empty = { 
    isEdit: false,
    articleCode: "", 
    collectionName: "", 
    groupColor: "", 
    origin: "", 
    deck: "", 
    stockStatus: "", 
    exw: "0", 
    moq: "0", 
    uom: "",
    retailBuy: "0",
    cartonDimL: "0",
    cartonDimW: "0",
    cartonDimH: "0",
    cartonGrossWeight: "0",
    pcsPerCarton: "1"
  };
  
  const [form, setForm] = useState(empty);
  const [initialForm, setInitialForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [hint, setHint] = useState("");
  const [errorSave, setErrorSave] = useState("");

  const [isNarrow, setIsNarrow] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 920 : false
  );
  
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= 920);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const qRef = useRef(null);

  // Trigger search execution queries after typing halts
  useEffect(() => {
    const t = setTimeout(() => loadList(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Execute base rehydration loop on interface component mount
  useEffect(() => {
    loadList("");
    setTimeout(() => qRef.current?.focus(), 0);
  }, []);

  async function loadList(term) {
    try {
      setLoadingList(true);
      setErrorList("");
      const u = new URL("/api/data", window.location.origin);
      u.searchParams.set("scope", "hd_stock");
      u.searchParams.set("sub", "list");
      if (term && term.trim()) u.searchParams.set("q", term.trim());

      const r = await fetch(u.toString(), { method: "GET" });
      const j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP " + r.status);
      setRows(Array.isArray(j.rows) ? j.rows : []);
    } catch (e) {
      setErrorList(String(e.message || e));
      setRows([]);
    } finally {
      setLoadingList(false);
    }
  }

  function selectRow(r) {
    const next = {
      isEdit: true,
      articleCode: String(r.article_code || ""),
      collectionName: String(r.collection_name || ""),
      groupColor: String(r.group_color || ""),
      origin: String(r.origin || ""),
      deck: String(r.deck || ""),
      stockStatus: String(r.stock_status || ""),
      exw: String(r.exw ?? "0"),
      moq: String(r.moq ?? "0"),
      uom: String(r.uom || ""),
      retailBuy: String(r.retail_buy ?? "0"),
      cartonDimL: String(r.carton_dim_l_mm ?? "0"),
      cartonDimW: String(r.carton_dim_w_mm ?? "0"),
      cartonDimH: String(r.carton_dim_h_mm ?? "0"),
      cartonGrossWeight: String(r.carton_gross_weight_kg ?? "0"),
      pcsPerCarton: String(r.pcs_per_carton ?? "1"),
    };
    setForm(next);
    setInitialForm(next);
    setHint("Record Profile Selected");
    setErrorSave("");
  }

  function newRow() {
    const next = { ...empty, isEdit: false };
    setForm(next);
    setInitialForm(next);
    setHint("Ready for new stock creation entry setup");
    setErrorSave("");
  }

  function resetForm() {
    setForm(initialForm);
    setHint("Changes reverted back to original states");
    setErrorSave("");
  }

  const changed = JSON.stringify(form) !== JSON.stringify(initialForm);

  function onChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function onSave() {
    setErrorSave("");
    setHint("");

    if (!String(form.articleCode || "").trim()) return setErrorSave("Article Code is a mandatory unique reference column field.");

    setSaving(true);
    try {
      let r, j;
      if (form.isEdit) {
        r = await fetch("/api/data?scope=hd_stock", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
      } else {
        r = await fetch("/api/data?scope=hd_stock", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
      }
      
      j = await r.json();
      if (!r.ok || !j.ok) throw new Error(j?.error || "HTTP Transaction Error code " + r.status);
      
      setHint(form.isEdit ? "Stock catalog file records updated successfully." : "New item line successfully committed to registry.");
      await loadList(q); 
      newRow(); 
    } catch (e) {
      setErrorSave(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }

  // ---------- Layout configurations ----------
  const grid = {
    display: "grid",
    gridTemplateColumns: isNarrow ? "1fr" : "380px 1fr",
    gap: isNarrow ? 10 : 20,
    alignItems: "start",
  };
  const fieldGrid = {
    display: "grid",
    gridTemplateColumns: isNarrow ? "1fr" : "200px 1fr",
    gap: "12px",
    alignItems: "center",
  };
  const smallText = { fontSize: isNarrow ? 11 : 12, opacity: 0.75 };

  return (
    <div className="app-page" style={{ maxWidth: "1350px" }}>
      <main className="container">
        <h1 className="menu-title" style={isNarrow ? { fontSize: 22, lineHeight: "24px" } : undefined}>
          Tuiss Web Tools — Purchase
        </h1>
        <h2 style={{ marginTop: -8, ...(isNarrow ? { fontSize: 16 } : {}) }}>Warehouse Master Inventory / hd_stock Maintenance</h2>

        {/* Action Command Controls Bar Row */}
        <div style={{ margin: "8px 0 16px", display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <Button onClick={() => nav("/purchase/config")}>← Trở về / Back</Button>
          <Button onClick={newRow}>New Item</Button>
          <Button onClick={resetForm} disabled={!changed}>Reset Form</Button>
          <Button 
            onClick={onSave} 
            disabled={saving || (!changed && form.isEdit)} 
            style={{ 
              backgroundColor: "#2563eb", 
              color: "#fff", 
              borderColor: "#1d4ed8",
              opacity: (saving || (!changed && form.isEdit)) ? 0.5 : 1,
              cursor: (saving || (!changed && form.isEdit)) ? "not-allowed" : "pointer"
            }}
          >
            {saving ? "Committing transaction..." : "Save Record changes"}
          </Button>
          {hint ? <span style={{ marginLeft: 8, color: "#166534", fontWeight: 600, ...smallText }}>{hint}</span> : null}
        </div>

        <div style={grid}>
          {/* LEFT PANEL VIEW: DATABASE SEARCH INDEX CODES LIST */}
          <section className="box" style={{ overflow: "hidden" }}>
            <label style={{ display: "block", fontWeight: 600, marginBottom: "6px" }}>
              Search Stock Registry (Article Code / Collection Name)
            </label>
            <input
              ref={qRef}
              className="text-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Type unique item string variables to target lookups..."
            />
            <div style={{ marginTop: 6, ...smallText, color: "#666" }}>
              {loadingList ? "Querying records..." : `Total matching records located: ${rows.length}`}
              {errorList ? <span style={{ color: "crimson", marginLeft: 8 }}>{errorList}</span> : null}
            </div>

            <div style={{ marginTop: 10, maxHeight: "560px", overflowY: "auto", borderTop: "1px solid #eee" }}>
              {rows.map((r, idx) => {
                const isSelectedRow = r.article_code === form.articleCode;
                // FIX: Composite element key prevents React array reconciliation freezing bugs
                return (
                  <button
                    key={`${r.article_code}-${idx}`}
                    onClick={() => selectRow(r)}
                    className="menu-card"
                    style={{
                      width: "100%",
                      display: "block",
                      textAlign: "left",
                      padding: "10px 12px",
                      border: "none",
                      borderBottom: "1px solid #f0f0f0",
                      borderRadius: 0,
                      boxShadow: "none",
                      background: isSelectedRow ? "#eeffe6" : "transparent",
                      fontSize: "14px"
                    }}
                  >
                    <div style={{ fontWeight: 700, color: "#111" }}>
                      Code: {r.article_code || "(null)"}
                    </div>
                    <div style={{ margin: "3px 0", color: "#444", fontSize: "13px" }}>
                      Collection: {r.collection_name || "-"} | Color: {r.group_color || "-"}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", color: "#777", fontSize: "11px" }}>
                      <span>Origin: {r.origin || "Unknown"} | Deck: {r.deck || "-"}</span>
                      <span style={{ fontWeight: "600", color: "#2563eb" }}>EXW: ${r.exw || 0}</span>
                    </div>
                  </button>
                );
              })}
              {rows.length === 0 && !loadingList ? <div style={{ padding: 12, ...smallText, textAlign: "center", color: "#888" }}>No active records matched parameter constraints.</div> : null}
            </div>
          </section>

          {/* RIGHT PANEL VIEW: MODIFICATION DATA FIELDS SHEET */}
          <section className="box">
            <h3 style={{ margin: "0 0 16px 0", fontSize: "15px", borderBottom: "1px solid #eee", paddingBottom: "8px", fontWeight: 600, color: "#333" }}>
              {form.isEdit ? `Edit Inventory Properties for Article [ ${form.articleCode} ]` : "Create New Item Master Specifications Sheet"}
            </h3>

            <div style={fieldGrid}>
              <label style={{ fontWeight: 600 }}>Article Code (Item SKU Key)</label>
              <input 
                className="text-input" 
                name="articleCode" 
                value={form.articleCode} 
                onChange={onChange} 
                placeholder="e.g., 0240731000155" 
                disabled={form.isEdit} 
                style={form.isEdit ? { backgroundColor: "#f3f4f6", color: "#6b7280" } : undefined}
              />

              <label style={{ fontWeight: 600 }}>Collection Name</label>
              <input className="text-input" name="collectionName" value={form.collectionName} onChange={onChange} placeholder="e.g., EOS500 TUBE 50 AL" />

              <label style={{ fontWeight: 600 }}>Group Color Variant</label>
              <input className="text-input" name="groupColor" value={form.groupColor} onChange={onChange} placeholder="e.g., BLACK / White / Silver" />

              <label style={{ fontWeight: 600 }}>Goods Origin Country</label>
              <input className="text-input" name="origin" value={form.origin} onChange={onChange} placeholder="e.g., Malaysia / Netherlands" />

              <label style={{ fontWeight: 600 }}>Sample Deck / Folder location</label>
              <input className="text-input" name="deck" value={form.deck} onChange={onChange} placeholder="e.g., Binder Volume 2A" />

              <label style={{ fontWeight: 600 }}>Current Stock Status</label>
              <input className="text-input" name="stockStatus" value={form.stockStatus} onChange={onChange} placeholder="e.g., Active / Phase Out / Discontinued" />

              <label style={{ fontWeight: 600 }}>EXW Factory Price Rate ($ USD)</label>
              <input className="text-input" type="number" name="exw" value={form.exw} onChange={onChange} min="0" step="any" />

              <label style={{ fontWeight: 600 }}>Minimum Order Quantity (MOQ)</label>
              <input className="text-input" type="number" name="moq" value={form.moq} onChange={onChange} min="0" />

              <label style={{ fontWeight: 600 }}>Unit of Measure (UOM)</label>
              <input className="text-input" name="uom" value={form.uom} onChange={onChange} placeholder="e.g., pcs / m / set / roll" />

              <label style={{ fontWeight: 600 }}>Retail Buy Price Rate ($ USD)</label>
              <input className="text-input" type="number" name="retailBuy" value={form.retailBuy} onChange={onChange} min="0" step="any" />

              {/* Master Carton Packaging Specifications Section */}
              <div style={{ gridColumn: "1 / -1", borderTop: "1px dashed #ccc", paddingTop: "12px", marginTop: "8px", fontWeight: "700", color: "#1e40af", fontSize: "13px" }}>
                📦 Master Carton Packaging Specifications (Bulk Shipping Engine)
              </div>

              <label style={{ fontWeight: 600 }}>Carton Length (mm)</label>
              <input className="text-input" type="number" name="cartonDimL" value={form.cartonDimL} onChange={onChange} min="0" placeholder="e.g., 600" />

              <label style={{ fontWeight: 600 }}>Carton Width (mm)</label>
              <input className="text-input" type="number" name="cartonDimW" value={form.cartonDimW} onChange={onChange} min="0" placeholder="e.g., 250" />

              <label style={{ fontWeight: 600 }}>Carton Height (mm)</label>
              <input className="text-input" type="number" name="cartonDimH" value={form.cartonDimH} onChange={onChange} min="0" placeholder="e.g., 170" />

              <label style={{ fontWeight: 600 }}>Carton Gross Weight (kg)</label>
              <input className="text-input" type="number" name="cartonGrossWeight" value={form.cartonGrossWeight} onChange={onChange} min="0" step="any" placeholder="e.g., 21.0" />

              <label style={{ fontWeight: 600 }}>Pieces per Master Carton</label>
              <input className="text-input" type="number" name="pcsPerCarton" value={form.pcsPerCarton} onChange={onChange} min="1" placeholder="e.g., 10" />
            </div>

            {errorSave ? (
              <div style={{ color: "crimson", marginTop: 14, fontWeight: 500, padding: "8px 12px", background: "#fef2f2", borderRadius: "6px", border: "1px solid #fca5a5" }}>{errorSave}</div>
            ) : (
              <div style={{ marginTop: 14, ...smallText, color: "#666", fontStyle: "italic" }}>Verify item profile definitions, then push the Save button to commit modifications down to the relational tables.</div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}